<template>
    <page-loading v-if="loading || !triggers.length || !actions.length"></page-loading>
    <form-container v-else>
        <step-form-heading v-if="!props.automationRuleId" :icon="faRobot">
            New Automation

            <template v-slot:description> Automatically run actions based on a triggering event. </template>
        </step-form-heading>

        <page-header v-if="props.automationRuleId" class="mb-6 lg:mb-12">
            <template v-slot:subtitle>
                <a href="/automation/event" class="no-underline! text-sky-600! text-base font-normal">
                    <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                    All Automations
                </a>
            </template>

            {{ data.name }}
        </page-header>

        <div
            :class="{
                'grid grid-cols-12': props.automationRuleId,
            }"
        >
            <page-sidebar
                v-if="props.automationRuleId"
                :links="[
                    { href: `/automation/event/${props.automationRuleId}`, label: 'Details' },
                    { href: `/automation/event/${props.automationRuleId}#filters`, label: 'Filters' },
                    { href: `/automation/event/${props.automationRuleId}#actions`, label: 'Actions' },
                ]"
            ></page-sidebar>
            <vx-sticky-nav
                :sections="[
                    { id: 'name', label: 'Name' },
                    { id: 'category', label: 'Category' },
                    { id: 'trigger', label: 'Trigger' },
                    { id: 'filters', label: 'Filters' },
                    { id: 'actions', label: 'Actions' },
                ]"
                class="col-span-12 lg:col-span-8 lg:col-start-3"
                :navClasses="`${props.automationRuleId ? 'hidden' : ''}`"
                contentClasses="flex mx-auto gap-2 md:gap-8 flex-col w-full max-w-(--breakpoint-lg) items-center"
            >
                <div v-if="data?.flow_id" data-test="alert-state" class="max-w-(--breakpoint-sm) w-full">
                    <flow-resource-state-alert
                        ref="flowAlertRef"
                        :resourceName="data.name"
                        :editing="!!props.automationRuleId"
                        :flowId="data.flow_id"
                        :flowSection="data.flow_section"
                    ></flow-resource-state-alert>
                </div>

                <vx-expandable-card id="name" class="max-w-(--breakpoint-sm)">
                    <template v-slot:header> Name </template>

                    <template v-slot:content>
                        <vx-input
                            v-model="data.name"
                            data-test="automation-name"
                            :error="displayFirstError(validation.name.$errors)"
                            autofocus
                            size="lg"
                            :maxlength="200"
                        ></vx-input>
                    </template>
                </vx-expandable-card>

                <resource-category
                    id="category"
                    v-model="data.category"
                    :disabled="!!data.flow_id"
                    @reset="data.category = null"
                    class="max-w-(--breakpoint-sm)"
                >
                </resource-category>

                <vx-expandable-card id="trigger" class="max-w-(--breakpoint-sm)">
                    <template v-slot:header> Trigger </template>
                    <template v-slot:headerSubtitle>
                        Select an event that will trigger this automation to run.
                    </template>

                    <template v-slot:content>
                        <vx-selectable
                            size="lg"
                            v-model="data.event"
                            data-test="automation-event"
                            :clearable="false"
                            :options="triggers"
                            :reduce="(option) => option.value"
                            label="label"
                            placeholder="Select a trigger..."
                            :error="displayFirstError(validation.event.$errors)"
                        >
                            <template v-slot:option="{ option }">
                                <vx-badge size="sm" class="px-1.5 mr-4" :color="option.color">
                                    <font-awesome-icon
                                        widthAuto
                                        class="text-xs!"
                                        :icon="option.icon"
                                    ></font-awesome-icon>
                                </vx-badge>
                                {{ option.label }}
                            </template>
                            <template v-slot:selected-option="{ option }">
                                <vx-badge size="sm" class="px-1.5 mr-4" :color="option.color">
                                    <font-awesome-icon
                                        widthAuto
                                        class="text-xs!"
                                        :icon="option.icon"
                                    ></font-awesome-icon>
                                </vx-badge>
                                {{ option.label }}
                            </template>
                        </vx-selectable>
                    </template>
                </vx-expandable-card>

                <div
                    id="filters"
                    class="w-full transition ease-out duration-300"
                    :class="!data.event && 'scale-95 opacity-40 pointer-events-none'"
                >
                    <automation-rule-form-matches
                        :error="displayFirstError(validation.match_groups) || maxMatchesError"
                    ></automation-rule-form-matches>
                </div>
                <div
                    id="actions"
                    class="w-full transition ease-out duration-300 delay-75"
                    :class="!data.event && 'scale-95 opacity-40 pointer-events-none'"
                >
                    <automation-rule-form-actions
                        :error="displayFirstError(validation.automated_actions)"
                    ></automation-rule-form-actions>
                </div>

                <vx-sticky-footer class="flex-wrap max-w-(--breakpoint-sm)">
                    <vx-button
                        href="/automation/event"
                        class="order-3 w-full lg:w-20 lg:order-1"
                        size="lg"
                        color="muted-dark"
                        outline
                    >
                        Cancel
                    </vx-button>
                    <vx-button
                        @click="submit()"
                        data-test="submit"
                        :loading="processing"
                        type="button"
                        class="order-1 w-full lg:w-auto lg:grow lg:order-3"
                        size="lg"
                        color="primary"
                    >
                        {{ props.automationRuleId ? 'Update' : 'Create' }} Automation
                    </vx-button>
                </vx-sticky-footer>
            </vx-sticky-nav>
        </div>
    </form-container>
</template>

<script setup>
import { faArrowLeft, faRobot, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxExpandableCard,
    VxInput,
    VxStickyFooter,
    VxStickyNav,
    VxSelectable,
    VxBadge,
} from '@voxie/frontend-components';
import { helpers, maxLength, required } from '@vuelidate/validators';
import { computed, onMounted, ref } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import { displayFirstError } from '~/utils/validation';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import { useAutomationRule } from './useAutomationRule';
import { useAutomationRulesMetadata } from './useAutomationRulesMetadata';
import AutomationRuleFormMatches from './AutomationRuleFormMatches.vue';
import AutomationRuleFormActions from './AutomationRuleFormActions.vue';
import { useUrlSearchParams } from '@vueuse/core';
import { automationRulesClient, flowsClient } from '~/services';
import ResourceCategory from '~/components/general/categories/ResourceCategory.vue';
import FormContainer from '~/components/general/FormContainer.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useToasted } from '~/composables/useToasted';
import { useFormSubmission } from '~/composables/useFormSubmission.js';
import FlowResourceStateAlert from '~/components/automation/flows/components/FlowResourceStateAlert.vue';

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    automationRuleId: {
        type: Number,
    },
});

const toasted = useToasted();

const { actions, triggers } = useAutomationRulesMetadata();
const { mount, data, loading, defaultData } = useAutomationRule();
const queryParams = useUrlSearchParams();

const flowAlertRef = ref();
const savedAutomationId = ref();

onMounted(async () => {
    await mount(props.automationRuleId || queryParams.duplicate_from_id);

    if (queryParams.duplicate_from_id) {
        // unsets the id and name when duplicating
        data.value.id = undefined;
        data.value.name = '';

        if (data.value.flow_id) {
            try {
                const {
                    data: {
                        config: { sections },
                    },
                } = await flowsClient.getConfig(teamId, data.value.flow_id);
                // find section
                data.value.flow_section = sections.findIndex((section) =>
                    section.resources.find(
                        (resource) =>
                            resource.type === 'automation' &&
                            Number(resource.id) === Number(queryParams.duplicate_from_id)
                    )
                );
            } catch (e) {
                console.error(e);
            }
        }
    }

    if (!data.value.flow_id && queryParams.flow_id) {
        data.value.flow_id = queryParams.flow_id;
        data.value.flow_section = Number(queryParams.flow_section || 0);
    }

    // Set the default value to 'uncategorized'
    data.value.category = null;
});

const maxMatchesError = computed(() => {
    const sum = (data.value.match_groups || []).reduce((count, matchGroup) => {
        return count + matchGroup.matches.length;
    }, 0);

    if (sum > 50) {
        return 'An automation can only contain 50 filters total';
    }

    return '';
});

const save = async () => {
    if (maxMatchesError.value) {
        toasted.error(maxMatchesError.value, {
            sticky: true,
        });
        return;
    }

    const response = await automationRulesClient.createOrUpdate(teamId, data.value);
    savedAutomationId.value = response.data.id;
};

const { submit, processing, validation } = useFormSubmission({
    data,
    validation: {
        name: { required, maxLength: maxLength(120) },
        event: { required },
        match_groups: {
            maxLength: helpers.withMessage('An automation can only contain 10 filter groups', maxLength(10)),
        },
        automated_actions: {
            maxLength: helpers.withMessage('An automation can only have 25 actions', maxLength(25)),
        },
    },
    preserveScroll: () => Boolean(props.automationRuleId),
    submit: save,
    reset: () => {
        if (!props.automationRuleId && !data.value.flow_id) {
            data.value = defaultData();
        }
    },
    notification: () =>
        data.value.flow_id
            ? props.automationRuleId
                ? {
                      message: 'Automation updated',
                      action: {
                          icon: faSquareArrowUpLeft,
                          label: 'Go to Flow',
                          href: flowAlertRef.value.flowLink,
                      },
                  }
                : {
                      message: 'Automation created',
                      redirect: flowAlertRef.value.flowLink,
                      action: {
                          icon: faSquareArrowUpLeft,
                          label: 'Edit Automation',
                          href: `/automation/event/${savedAutomationId.value}`,
                      },
                  }
            : {
                  message: `Automation ${props.automationRuleId ? 'updated' : 'created'}`,
                  action: {
                      icon: faSquareArrowUpLeft,
                      label: 'Back to Automations',
                      href: '/automation/event',
                  },
              },
});
</script>
